import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  model,
  signal,
  untracked,
  viewChild,
  resource,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrDatagrid, ClrForm, ClrLoadingState } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';

import { ArrayMethodsCallbackPipe } from 'src/app/pipes/array-methods-callback.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { LinieDrehungPipe } from 'src/app/pipes/linie-drehung.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { LinieDrehungService } from 'src/app/services/Shared/linie-drehung.service';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { Table } from 'src/app/models/ui/table';

import { EntsperrAnlageService } from 'src/app/services/Shared/entsperr-anlage.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { DatalistInputComponent } from '../../_shared/datalist-input/datalist-input.component';
import { calcPosition, GeoAutofill } from 'src/app/services/Shared/geo-autofill';
import { ifNotError } from 'src/app/misc/compositions';
import { calcDirection, LinieValidationManager } from 'src/app/shared/classes/linie-validation-manager';
import { ObjTypePipe } from 'src/app/pipes/objtype.pipe';
import { errorHandle } from 'src/app/misc/functions';

@Component({
  selector: 'app-grundinstandsetzungdialog',
  templateUrl: './grundinstandsetzungdialog.component.html',
  styleUrl: './grundinstandsetzungdialog.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    DatalistInputComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    LinieDrehungPipe,
    ArrayMethodsCallbackPipe,
    ObjTypePipe,
  ],
  providers: [LinieDrehungPipe, GeoAutofill],
})
export class GrundinstandsetzungdialogComponent extends LinieValidationManager {
  private readonly apiService = inject(APIService);
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly confirmService = inject(ConfirmService);
  private readonly geoAutofill = inject(GeoAutofill);
  protected readonly drehungPipe = inject(LinieDrehungPipe);
  protected readonly drehung = inject(LinieDrehungService);

  public readonly closedialog = new BehaviorSubject(false);
  private dataRefresh: EventEmitter<any>;

  private readonly grid = viewChild(ClrDatagrid);
  private readonly clrForm = viewChild(ClrForm);

  readonly bezeichnung = model<string>(undefined);
  readonly otypid = model<number>(undefined);
  readonly typ_Line_Point = model<number>(undefined);
  readonly opruefid = model<number>(undefined);
  readonly ostammid = model<number>(undefined);
  readonly stammdata = model<{ startmeter: number; endmeter: number }>({ startmeter: 0, endmeter: 0 });

  private readonly startmeter = computed(() => this.stammdata().startmeter);
  private readonly endmeter = computed(() => this.stammdata().endmeter);
  protected readonly minMeter = computed(() => Math.min(this.startmeter(), this.endmeter()));
  protected readonly direction = computed(() => calcDirection(this.startmeter(), this.endmeter()));
  protected readonly selectedGI = signal<GIRow>(undefined);

  protected exceptStartEnd = (col: any) => !['von', 'bis'].includes(col.id);
  protected readonly table = signal<Table<GIRow>>({ columns: [], rows: [] });
  private readonly grundArtenResource = resource({
    params: this.otypid,
    loader: ({ params: otypid }) => this.apiService.getGrundInstandTypen(otypid),
  });
  protected readonly artListe = ifNotError(this.grundArtenResource);
  protected readonly isInspektion = signal(true);
  protected readonly saveState = signal(ClrLoadingState.DEFAULT);
  protected readonly geoState = signal(ClrLoadingState.DEFAULT);

  public set data(dataIn: any) {
    this.isInspektion.set(false);
    if (dataIn.table) this.table.set(dataIn.table);
    this.ostammid.set(dataIn.ostamm);
    this.otypid.set(dataIn.otypid);
    this.stammdata.set(dataIn.stammData);
    this.bezeichnung.set(dataIn.name);
    this.dataRefresh = dataIn.dataRefresh;
  }

  protected formGITop = new FormGroup({
    von: new FormControl<number>(null),
    bis: new FormControl<number>(null),
    Grundinstandsetzung: new FormControl<string>('', [Validators.required]),
  });

  constructor() {
    super({ start: 'von', end: 'bis' }, true);

    effect(() => {
      if (!(this.startmeter() - this.endmeter())) return;
      this.initLinieValidators(this.startmeter(), this.endmeter(), !this.isInspektion(), this.formGITop);
    });

    inject(EntsperrAnlageService).registerDestroyRef(inject(DestroyRef));

    this.geoAutofill.registerOSTAMMID(this.ostammid);

    effect(() => {
      if (this.opruefid() && (this.isInspektion() || !this.table().columns.length)) untracked(() => this.freshReload());
    });

    effect(() => {
      const selected = this.selectedGI();
      untracked(() => {
        this.formGITop.reset();
        if (!selected) return;
        this.formGITop.patchValue(selected);
        this.clrForm().markAsTouched();
      });
    });
  }

  protected addGI() {
    this.formGITop.reset();
    this.grid()?.selection.clearSelection();
  }

  protected async deleteGI() {
    const text = this.mrTranslate.transform('Eintrag wirklich löschen?');
    const ok = await this.confirmService.danger(text);
    if (!ok || !this.selectedGI()) return;

    const omazsid = this.selectedGI().id;
    try {
      await this.apiService.deleteMangel(omazsid);
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.freshReload();
      this.grid()?.selection.clearSelection();
      this.dataRefresh?.emit(true);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Fehler beim Löschen'));
    }
  }

  private async freshReload() {
    const GRInstTable = await this.apiService.getGrundInstand(this.opruefid(), this.ostammid(), this.typ_Line_Point());
    this.table.update(table => {
      if (!table?.columns?.length) table = GRInstTable;
      else table.rows = GRInstTable.rows;
      return { ...table };
    });
  }

  protected saveIfValid() {
    this.clrForm().markAsTouched();
    if (this.formGITop.valid) this.saveGI();
  }

  private async saveGI() {
    this.saveState.set(ClrLoadingState.LOADING);

    const sendObj: GIModel = {
      OMAZSID: this.selectedGI()?.id,
      table: this.formGITop.value,
      ids: {
        OSTAMMID: this.ostammid(),
        OPRUEFID: this.opruefid(),
        OTYPID: this.otypid(),
        typ_Line_Point: this.typ_Line_Point(),
      },
    };
    try {
      const { success, error, id } = await this.apiService.setGrundInstand(sendObj);
      if (!success) throw new Error(error);

      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      this.freshReload();
      this.grid().selected = [{ id }];
      this.dataRefresh?.emit(true);
      this.saveState.set(ClrLoadingState.SUCCESS);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Fehler beim Speichern'));
      this.saveState.set(ClrLoadingState.DEFAULT);
    }
  }

  public checkLinieValid(startmeter: number, endmeter: number) {
    this.stammdata.set({ startmeter, endmeter });

    const valid = this.isLinieValid(this.table().rows, startmeter, endmeter);
    if (!valid)
      this.toastr.error(
        this.mrTranslate.transform('Berücksichtigen Sie die Start- & Endmeter'),
        this.mrTranslate.transform('Grundinstandsetzung')
      );
    return valid;
  }

  public selectedChange(event) {
    this.selectedGI.set(event[0]);
  }

  protected async findPositionAndAutofill(mode: 'von' | 'bis') {
    this.geoState.set(ClrLoadingState.LOADING);
    const snapped = await this.geoAutofill.getSnappedLength();
    this.geoState.set(ClrLoadingState.DEFAULT);
    if (snapped === null) return;

    const position = calcPosition({
      snapped,
      start: this.startmeter(),
      end: this.endmeter(),
      direction: this.direction(),
    });

    this.formGITop.controls[mode].setValue(position);
  }
  protected close() {
    this.closedialog.next(true);
  }
}

export interface GIRow {
  id: number;
  ostammid: number;
  von: number;
  bis: number;
  Grundinstandsetzung: string;
  bild: string;
  benutzername: string;
  status: string;
}

export interface GIModel {
  OMAZSID: number;
  table: Partial<{
    von: number;
    bis: number;
    Grundinstandsetzung: string;
  }>;
  ids: {
    OPRUEFID: number;
    OTYPID: number;
    OSTAMMID: number;
    typ_Line_Point: number;
  };
}
